"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { CalendarHeart, Clock, Tag, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const perks = [{
  title: "Members-Only Events",
  description: "Exclusive access to wellness workshops, expert talks, and VIP community gatherings.",
  icon: CalendarHeart,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/perk_events_exclusive.png"
}, {
  title: "Private Booking",
  description: "Priority scheduling, extended hours, and dedicated wellness rooms reserved just for you.",
  icon: Clock,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/perk_private_booking.png"
}, {
  title: "Special Pricing",
  description: "Preferred rates on all IV therapies, vitamin injections, lab testing, and retail products.",
  icon: Tag,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/perk_special_pricing.png"
}, {
  title: "First Access",
  description: "Be the first to experience our newest protocols, advanced therapies, and seasonal services.",
  icon: Sparkles,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652321/img/perk_first_access.png"
}];
export default function MembersOnlyPerks() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section id="membersonlyperks" className="bg-background py-32 lg:py-48 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-secondary/20 to-background pointer-events-none" />

      <div ref={ref} className={`container mx-auto px-4 md:px-6 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"}`}>
        <div className="text-center mb-20 lg:mb-32">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 tracking-tight">
            Members-Only Perks
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-2xl mx-auto font-sans font-light">
            Elevate your wellness journey with exclusive benefits designed for our most dedicated clients at M / O Protocol.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {perks.map((perk, i) => <Card key={i} data-index={i} className="bg-background/60 backdrop-blur-xl border-[var(--color-green-accent)]/40 hover:border-[var(--color-green-accent)] transition-all duration-700 overflow-hidden group shadow-lg shadow-black/5 rounded-2xl flex flex-col h-full">
              <div className="relative overflow-hidden">
                <AspectRatio ratio={4 / 3}>
                  <img src={perk.image} data-wvc-srcset={`${perk.image}-wvc-srcset`} data-wvc-sizes={`${perk.image}-wvc-sizes`} alt={perk.title} className="object-cover w-full h-full saturate-50 group-hover:saturate-100 transition-all duration-1000 group-hover:scale-105" loading="lazy" />
                </AspectRatio>
                <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/20 to-transparent" />
                <div className="absolute bottom-5 left-6 bg-background/80 backdrop-blur-md p-3 rounded-full border border-[var(--color-green-accent)]/30 text-[var(--color-green-accent)] shadow-sm">
                  <perk.icon strokeWidth={1} className="w-6 h-6" />
                </div>
              </div>
              <CardHeader className="pt-6 pb-3 px-6">
                <CardTitle className="font-default text-2xl text-foreground font-medium">
                  {perk.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-6 pb-8 flex-grow">
                <CardDescription className="text-base text-muted-foreground leading-relaxed font-sans">
                  {perk.description}
                </CardDescription>
              </CardContent>
            </Card>)}
        </div>

        <div className="mt-20 lg:mt-32 text-center">
          <Button asChild size="lg" className="bg-[var(--color-green-accent)] text-white hover:bg-[var(--color-green-accent)]/90 transition-colors duration-300 rounded-full px-10 py-6 h-auto text-lg font-sans tracking-wide">
            <Link to="/MembershipsPage">Join Membership</Link>
          </Button>
        </div>
      </div>
    </section>;
}